import {
  Search,
  Check,
  Mail,
  CreditCard,
  IdCard,
  Building2,
  Landmark,
  ShieldCheck,
  PenLine,
} from "lucide-react";

import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import RefreshButton from "../../../Component/Common/RefreshButton";
import Pagination from "../../../Component/Common/Pagination";
import RegularLoader from "../../../Component/Loader/RegularLoader";

import { useEffect, useState } from "react";
import { BASE_URL } from "../../../config/api";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatDateTimeSplit } from "../../../Component/Common/formatDateTime";

const STEP_SHORT_LABELS = [
  "Mail+WA",
  "Payment",
  "PAN",
  "GST",
  "Bank",
  "Agreement",
  "E-Sign",
];

// Builds the detail rows shown inside each step's hover popover,
// pulling real fields off the raw retailer record.
function getStepDetails(r, stepIndex) {
  switch (stepIndex) {
    case 0: {
      return {
        icon: Mail,
        fields: [
          { label: "Email verified", value: r.is_mail_verified === 1 ? "Yes" : "No" },
          { label: "WhatsApp verified", value: r.is_wa_verified === 1 ? "Yes" : "No" },
          { label: "Contact email", value: r.contact_email || "—" },
        ],
      };
    }
    case 1: {
      return {
        icon: CreditCard,
        fields: [
          { label: "Payment status", value: r.is_payment_success === 1 ? "Success" : "Not paid" },
          { label: "Pay now", value: r.pay_now === 1 ? "Enabled" : "Disabled" },
          { label: "Wallet balance", value: `₹${r.wallet_balance ?? 0}` },
        ],
      };
    }
    case 2: {
      const pan = r.pan_json;
      return {
        icon: IdCard,
        fields: [
          { label: "PAN", value: r.pan || "Not submitted" },
          { label: "Registered name", value: pan?.registered_name || "—" },
          { label: "Status", value: pan ? pan.status : "Pending" },
        ],
      };
    }
    case 3: {
      if (r.business_type === "unregistered") {
        return {
          icon: Building2,
          fields: [
            { label: "Business type", value: "Unregistered" },
            { label: "GST", value: "Not required" },
          ],
        };
      }
      const gst = r.gst_json;
      return {
        icon: Building2,
        fields: [
          { label: "GSTIN", value: r.gst || "Not submitted" },
          { label: "Legal name", value: gst?.legal_name_of_business || "—" },
          { label: "GST status", value: gst?.gst_in_status || "Pending" },
        ],
      };
    }
    case 4: {
      return {
        icon: Landmark,
        fields: [
          { label: "Bank name", value: r.bank_name || "Not added" },
          { label: "Account no.", value: r.account_no || "—" },
          { label: "IFSC", value: r.ifsc_code || "—" },
        ],
      };
    }
    case 5: {
      const updated = formatDateTimeSplit(r.updated_at);
      return {
        icon: ShieldCheck,
        fields: [
          { label: "Verification stage", value: r.is_verified ?? "Not started" },
          { label: "status", value: r.is_verified === 7 ? "Approved" : "In review" },
          {
            label: "Updated At",
            value: (
              <>
                <div>{updated.date}</div>
                <small>{updated.time}</small>
              </>
            )
          },
        ],
      };
    }
    case 6: {
      return {
        icon: PenLine,
        fields: [
          { label: "E-Sign status", value: r.esign_verified === 1 ? "Signed" : "Pending" },
          { label: "Document ID", value: r.document_id || "—" },
        ],
      };
    }
    default:
      return { icon: ShieldCheck, fields: [] };
  }
}

function StepPopover({ step, details, isDone, isActive, isSkipped, align }) {
  const Icon = details.icon;

  const alignClass =
    align === "left"
      ? "left-0"
      : align === "right"
      ? "right-0"
      : "left-1/2 -translate-x-1/2";

  return (
    <div
      className={`absolute top-7 ${alignClass} w-52 sm:w-60 max-w-[78vw] rounded-xl border border-slate-200 bg-white shadow-lg p-3 z-30 text-left`}
    >
      <div className="flex items-center gap-2 mb-2.5 pb-2 border-b border-slate-100">
        <div
          className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
            isDone
              ? "bg-blue-50 text-blue-600"
              : isSkipped
              ? "bg-amber-50 text-amber-600"
              : isActive
              ? "bg-blue-50 text-blue-500"
              : "bg-slate-100 text-slate-400"
          }`}
        >
          <Icon size={13} strokeWidth={2.25} />
        </div>
        <span className="text-[12px] sm:text-[12.5px] font-semibold text-slate-700">
          {step.label}
        </span>
      </div>

      <div className="space-y-1.5">
        {details.fields.map((f) => (
          <div
            key={f.label}
            className="flex items-center justify-between gap-3"
          >
            <span className="text-[11px] text-slate-400 shrink-0">{f.label}</span>
            <div className="text-[11px] font-medium text-slate-700 text-right max-w-[120px] truncate">
              {typeof f.value === "string" || typeof f.value === "number"
                ? f.value
                : f.value}
            </div>
          </div>
        ))}
      </div>

      {isSkipped && (
        <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-start gap-1.5">
          <span className="text-[10px] font-semibold text-amber-600 shrink-0">
            Skipped:
          </span>
          <span className="text-[10.5px] text-slate-500 leading-snug">
            A later step is already complete — this one was bypassed.
          </span>
        </div>
      )}

      {isActive && (
        <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-start gap-1.5">
          <span className="text-[10px] font-semibold text-blue-500 shrink-0">
            Next:
          </span>
          <span className="text-[10.5px] text-slate-500 leading-snug">
            Complete this step to move the retailer forward.
          </span>
        </div>
      )}
    </div>
  );
}

function Stepper({ steps, rawRetailer }) {
  // index of the first incomplete step = the "current" / active step
  const activeIndex = steps.findIndex((s) => !s.done);
  const allDone = activeIndex === -1;

  // A step is "skipped" if it isn't done, but a LATER step IS done —
  // meaning onboarding moved past it without this one being completed
  // (e.g. PAN done, Payment still null, GST already done).
  const lastDoneIndex = steps.reduce(
    (acc, s, i) => (s.done ? i : acc),
    -1
  );

  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    // No fixed min-width here — the stepper fills whatever space its
    // parent gives it and the dots/connectors shrink together as one unit.
    <div className="flex items-center w-full">
      {steps.map((step, i) => {
        const isDone = step.done;
        const isSkipped = !isDone && i < lastDoneIndex;
        const isActive = !allDone && !isSkipped && i === activeIndex;
        const isLast = i === steps.length - 1;
        const isFirst = i === 0;
        const details = getStepDetails(rawRetailer, i);

        // keep popovers on-screen near the edges of the stepper
        const align = isFirst ? "left" : isLast ? "right" : "center";

        return (
          <div
            key={step.label}
            className={`flex items-center relative ${
              isLast ? "" : "flex-1"
            }`}
          >
            <div
              className="relative flex flex-col items-center"
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {isDone ? (
                <div className="w-[18px] h-[18px] sm:w-6 sm:h-6 rounded-full bg-blue-600 flex items-center justify-center shadow-sm shrink-0 cursor-pointer">
                  <Check
                    size={10}
                    strokeWidth={3}
                    className="text-white sm:w-[13px] sm:h-[13px]"
                  />
                </div>
              ) : isSkipped ? (
                <div className="w-[18px] h-[18px] sm:w-6 sm:h-6 rounded-full bg-amber-100 border-2 border-amber-400 flex items-center justify-center shrink-0 cursor-pointer">
                  <span className="text-amber-600 text-[10px] sm:text-[12px] font-bold leading-none">
                    !
                  </span>
                </div>
              ) : isActive ? (
                <div className="relative w-[18px] h-[18px] sm:w-6 sm:h-6 shrink-0 cursor-pointer">
                  <div className="absolute inset-0 rounded-full bg-blue-400/30 animate-pulse" />
                  <div className="absolute inset-0 rounded-full ring-2 sm:ring-4 ring-blue-100" />
                  <div className="relative w-[18px] h-[18px] sm:w-6 sm:h-6 rounded-full bg-blue-600 flex items-center justify-center shadow-[0_0_0_3px_rgba(37,99,235,0.15)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  </div>
                </div>
              ) : (
                <div className="w-[18px] h-[18px] sm:w-6 sm:h-6 rounded-full border-2 border-slate-200 bg-white shrink-0 cursor-pointer" />
              )}

              {hoveredIndex === i && (
                <StepPopover
                  step={step}
                  details={details}
                  isDone={isDone}
                  isActive={isActive}
                  isSkipped={isSkipped}
                  align={align}
                />
              )}
            </div>

            {!isLast && (
              <div
                className={`h-[2px] sm:h-[2.5px] flex-1 mx-0.5 sm:mx-1 rounded-full ${
                  isDone
                    ? "bg-blue-600"
                    : isSkipped
                    ? "bg-amber-300"
                    : "bg-slate-200"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function StepsProgressTable({
  loading,
  search,
  searchRetailers,
  fetchRetailers,
  currentPage,
  totalPages,
  setCurrentPage,
  paginatedRetailers,
  navigate,
}) {

  const internalNavigate = useNavigate();
  const navigateTo = navigate || internalNavigate;
  const [internalRetailers, setInternalRetailers] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);

  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [internalTotalPages, setInternalTotalPages] = useState(1);
  const [internalTotalRecords, setInternalTotalRecords] = useState(0);

  


const hasPropData =
  Array.isArray(paginatedRetailers) &&
  paginatedRetailers.length > 0; 

  

  const fetchInternalRetailers = async () => {

    try {

      setInternalLoading(true);

      const session = JSON.parse(
        localStorage.getItem(
          "authUserData"
        ) || "{}"
      );

      const role =
        session?.role;

      const id =
        session?.id;

      const state =
        session?.state;

      const district =
        session?.district;

      const locationMode =
        session?.location_mode;

      const handle =
        session?.handle || [];

      const params = {
        role: 5,

        page:
          internalCurrentPage,
      };

      // ROLE 2
      if (role === 2) {
        params.company_id = id;
      }

      // ROLE 3
      if (role === 3) {

        if (
          Array.isArray(handle) &&
          handle.includes("AGENT")
        ) {

          if (
            locationMode ===
              "state" &&
            state
          ) {
            params.state = state;
          }

          if (
            locationMode ===
              "district" &&
            district
          ) {
            params.district =
              district;
          }
        }
      }

      // ROLE 4
      if (role === 4) {
        params.agent_id = id;
      }

      // SEARCH
      if (search?.trim()) {
        params.search_value =
          search;
      }

      const response =
        await axios.get(
          `${BASE_URL}common/get-users`,
          {
            params,

            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const json =
        response.data;

      if (json?.status) {

        setInternalRetailers(
          json.data || []
        );

        setInternalCurrentPage(
          json?.pagination
            ?.current_page || 1
        );

        setInternalTotalPages(
          json?.pagination
            ?.last_page || 1
        );

        setInternalTotalRecords(
          json?.pagination
            ?.total || 0
        );
      }

    } catch (err) {

      console.error(
        "StepsProgressTable Fetch Error:",
        err
      );

    } finally {

      setInternalLoading(false);

    }
  };

useEffect(() => {
  if (!hasPropData) {
    fetchInternalRetailers();
  }
}, [internalCurrentPage, search]);

const retailersData = hasPropData
  ? paginatedRetailers
  : internalRetailers;

  const getRetailerSteps = (r) => {
    const step1 =
      r.is_mail_verified === 1 &&
      r.is_wa_verified === 1;

    const step2 =
      r.is_payment_success === 1;

    const step3 =
      r.pan_verified === 1 &&
      r.pan_json !== null;

    const step4 =
      r.business_type === "unregistered"
        ? true
        : r.gst_verified === 1 &&
          r.gst_json !== null;

    const step5 =
      r.bank_verified === 1 &&
      r.bank_json !== null;

    const step6 =
      r.is_verified === 7;

    const step7 =
      r.esign_verified === 1;

    return [
      {
        label: "Mail + WhatsApp Verified",
        done: step1,
      },
      {
        label: "Payment Success",
        done: step2,
      },
      {
        label: "PAN Verified",
        done: step3,
      },
      {
        label: "GST Verified",
        done: step4,
      },
      {
        label: "Bank Verified",
        done: step5,
      },
      {
        label: "Admin Verification",
        done: step6,
      },
      {
        label: "E-Sign Completed",
        done: step7,
      },
    ];
  };

  const refreshretailer = fetchRetailers || fetchInternalRetailers;

  return (
    <SectionCard
      title="Retailer Onboarding Progress"
      right={
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-2 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-lg border border-slate-200 bg-white shadow-sm focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 transition-all flex-1 sm:flex-initial min-w-0">
            <Search className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={2} />

            <input
              placeholder="Search retailer..."
              className="bg-transparent text-[13px] text-slate-700 outline-none placeholder:text-slate-400 w-full sm:w-36 lg:w-48 min-w-0"
              value={search}
              onChange={(e) =>
                searchRetailers(e.target.value)
              }
            />
          </div>

          <div className="shrink-0">
            <RefreshButton
              onClick={() =>
                refreshretailer(currentPage)
              }
            />
          </div>
        </div>
      }
    >
      {(loading || internalLoading) && (
        <RegularLoader />
      )}

      {!(loading || internalLoading) && (
        <>
          {retailersData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 sm:py-20 text-center px-4">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Search className="w-5 h-5 text-slate-400" strokeWidth={1.75} />
              </div>
              <p className="text-[14px] font-semibold text-slate-700">
                No retailers found
              </p>
              <p className="text-[13px] text-slate-400 mt-1">
                Try adjusting your search or check back later.
              </p>
            </div>
          ) : (
            <div className="space-y-3 w-full overflow-hidden">
              {/* Legend row — desktop only; mobile/tablet stack the stepper below the name instead */}
              <div className="hidden xl:flex items-center gap-1 pr-6 pb-1">
                <div className="w-[200px] shrink-0" />
                {STEP_SHORT_LABELS.map((label, i) => (
                  <div
                    key={label}
                    className={`text-[10.5px] font-medium text-slate-400 mr-3 ${
                      i === STEP_SHORT_LABELS.length - 1 ? "" : "flex-1"
                    }`}
                  >
                    {label}
                  </div>
                ))}
              </div>

              {retailersData.map((r) => {
                const retailerData = r.raw || r;

                const steps =
                  getRetailerSteps(retailerData);

                return (
                  <div
                    key={r.id}
                    className="group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-3 sm:px-5 py-3.5 sm:py-4 rounded-xl border border-slate-200 bg-white hover:border-blue-200 hover:shadow-md transition-all w-full min-w-0"
                  >
                    <div
                      className="w-full sm:w-[160px] lg:w-[200px] shrink-0 cursor-pointer min-w-0"
                      onClick={() =>
                        navigateTo(`/user/${r.id}`, {
                          state: {
                            entityType: "retailer",
                            data: r,
                          },
                        })
                      }
                    >
                      <div className="font-semibold text-slate-800 text-[13.5px] leading-tight truncate">
                        {r.business_name}
                      </div>
                      <div className="text-[12px] text-slate-400 mt-0.5 truncate">
                        {r.company_code}
                      </div>
                    </div>

                    <div className="flex-1 py-1 sm:py-2 min-w-0">
                      <Stepper steps={steps} rawRetailer={retailerData} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {(hasPropData ? totalPages : internalTotalPages) > 1 && (
            <div className="mt-5 flex justify-center sm:justify-end">
              <Pagination
                currentPage={
                  hasPropData
                    ? currentPage
                    : internalCurrentPage
                }
                totalPages={
                  hasPropData
                    ? totalPages
                    : internalTotalPages
                }
                onPageChange={
                  hasPropData
                    ? setCurrentPage
                    : setInternalCurrentPage
                }
              />
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}

// import {
//   Search,
//   CheckCircle,
//   Clock,
// } from "lucide-react";

// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import Pagination from "../../../Component/Common/Pagination";
// import RegularLoader from "../../../Component/Loader/RegularLoader";

// import { useEffect, useState } from "react";
// import { BASE_URL } from "../../../config/api";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";

// export default function StepsProgressTable({
//   loading,
//   search,
//   searchRetailers,
//   fetchRetailers,
//   currentPage,
//   totalPages,
//   setCurrentPage,
//   paginatedRetailers,
//   navigate,
// }) {

//   const internalNavigate = useNavigate();
//   const navigateTo = navigate || internalNavigate;
//   const [internalRetailers, setInternalRetailers] = useState([]);
//   const [internalLoading, setInternalLoading] = useState(false);

//   const [internalCurrentPage, setInternalCurrentPage] = useState(1);
//   const [internalTotalPages, setInternalTotalPages] = useState(1);
//   const [internalTotalRecords, setInternalTotalRecords] = useState(0);

  


// const hasPropData =
//   Array.isArray(paginatedRetailers) &&
//   paginatedRetailers.length > 0; 

  

//   const fetchInternalRetailers = async () => {

//     try {

//       setInternalLoading(true);

//       const session = JSON.parse(
//         localStorage.getItem(
//           "authUserData"
//         ) || "{}"
//       );

//       const role =
//         session?.role;

//       const id =
//         session?.id;

//       const state =
//         session?.state;

//       const district =
//         session?.district;

//       const locationMode =
//         session?.location_mode;

//       const handle =
//         session?.handle || [];

//       const params = {
//         role: 5,

//         page:
//           internalCurrentPage,
//       };

//       // ROLE 2
//       if (role === 2) {
//         params.company_id = id;
//       }

//       // ROLE 3
//       if (role === 3) {

//         if (
//           Array.isArray(handle) &&
//           handle.includes("AGENT")
//         ) {

//           if (
//             locationMode ===
//               "state" &&
//             state
//           ) {
//             params.state = state;
//           }

//           if (
//             locationMode ===
//               "district" &&
//             district
//           ) {
//             params.district =
//               district;
//           }
//         }
//       }

//       // ROLE 4
//       if (role === 4) {
//         params.agent_id = id;
//       }

//       // SEARCH
//       if (search?.trim()) {
//         params.search_value =
//           search;
//       }

//       const response =
//         await axios.get(
//           `${BASE_URL}common/get-users`,
//           {
//             params,

//             headers: {
//               Accept:
//                 "application/json",

//               "Content-Type":
//                 "application/json",
//             },
//           }
//         );

//       const json =
//         response.data;

//       if (json?.status) {

//         setInternalRetailers(
//           json.data || []
//         );

//         setInternalCurrentPage(
//           json?.pagination
//             ?.current_page || 1
//         );

//         setInternalTotalPages(
//           json?.pagination
//             ?.last_page || 1
//         );

//         setInternalTotalRecords(
//           json?.pagination
//             ?.total || 0
//         );
//       }

//     } catch (err) {

//       console.error(
//         "StepsProgressTable Fetch Error:",
//         err
//       );

//     } finally {

//       setInternalLoading(false);

//     }
//   };

// useEffect(() => {
//   if (!hasPropData) {
//     fetchInternalRetailers();
//   }
// }, [internalCurrentPage, search]);

// const retailersData = hasPropData
//   ? paginatedRetailers
//   : internalRetailers;

//   const getRetailerSteps = (r) => {
//     const step1 =
//       r.is_mail_verified === 1 &&
//       r.is_wa_verified === 1;

//     const step2 =
//       r.is_payment_success === 1;

//     const step3 =
//       r.pan_verified === 1 &&
//       r.pan_json !== null;

//     const step4 =
//       r.business_type === "unregistered"
//         ? true
//         : r.gst_verified === 1 &&
//           r.gst_json !== null;

//     const step5 =
//       r.bank_verified === 1 &&
//       r.bank_json !== null;

//     const step6 =
//       r.is_verified === 7;

//     const step7 =
//       r.esign_verified === 1;

//     return [
//       {
//         label: "Mail + WhatsApp Verified",
//         done: step1,
//       },
//       {
//         label: "Payment Success",
//         done: step2,
//       },
//       {
//         label: "PAN Verified",
//         done: step3,
//       },
//       {
//         label: "GST Verified",
//         done: step4,
//       },
//       {
//         label: "Bank Verified",
//         done: step5,
//       },
//       {
//         label: "Admin Verification",
//         done: step6,
//       },
//       {
//         label: "E-Sign Completed",
//         done: step7,
//       },
//     ];
//   };

//   const refreshretailer = fetchRetailers || fetchInternalRetailers;

//   return (
//     <SectionCard
//       title="Retailer Onboarding Progress"
//       right={
//         <div className="flex items-center gap-2 text-[11px]">
//           <div className="flex items-center gap-1 px-2 py-1 rounded-xl border border-slate-200 bg-slate-50">
//             <Search className="w-5 h-5" />

//             <input
//               placeholder="Search retailer..."
//               className="bg-transparent text-[11px] outline-none"
//               value={search}
//               onChange={(e) =>
//                 searchRetailers(e.target.value)
//               }
//             />
//           </div>

//           <div className="hidden lg:block">
//             <RefreshButton
//               onClick={() =>
//                 refreshretailer(currentPage)
//               }
//             />
//           </div>
//         </div>
//       }
//     >
//       {(loading || internalLoading) && (
//         <RegularLoader />
//       )}

//       {!(loading || internalLoading) && (
//         <>
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-sm text-left border border-slate-200 rounded-lg">
//               <thead className="bg-slate-50 border-b">
//                 <tr>
//                   <th className="px-4 py-2">
//                     Retailer
//                   </th>

//                   <th className="px-4 py-2 text-center">
//                     Mail + WA
//                   </th>

//                   <th className="px-4 py-2 text-center">
//                     Payment
//                   </th>

//                   <th className="px-4 py-2 text-center">
//                     PAN
//                   </th>

//                   <th className="px-4 py-2 text-center">
//                     GST
//                   </th>

//                   <th className="px-4 py-2 text-center">
//                     Bank
//                   </th>

//                   <th className="px-4 py-2 text-center">
//                     Agreement
//                   </th>

//                   <th className="px-4 py-2 text-center">
//                     E-Sign
//                   </th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {retailersData.map((r) => {
//                   const retailerData = r.raw || r;

//                   const steps =
//                     getRetailerSteps(retailerData);

//                   return (
//                     <tr
//                       key={r.id}
//                       className="border-b hover:bg-slate-50 cursor-pointer"
//                       onClick={() =>
//                         navigateTo(`/user/${r.id}`, {
//                           state: {
//                             entityType: "retailer",
//                             data: r,
//                           },
//                         })
//                       }
//                     >
//                       <td className="px-4 py-2">
//                         <div className="font-semibold">
//                           {r.business_name}
//                         </div>

//                         <div className="text-xs text-slate-500">
//                           {r.company_code}
//                         </div>
//                       </td>

//                       {steps.map((step) => (
//                         <td
//                           key={step.label}
//                           className="px-4 py-2 text-center"
//                         >
//                           {step.done ? (
//                             <CheckCircle
//                               size={18}
//                               className="text-green-600 mx-auto"
//                             />
//                           ) : (
//                             <Clock
//                               size={18}
//                               className="text-amber-500 mx-auto"
//                             />
//                           )}
//                         </td>
//                       ))}
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>

//           {(hasPropData ? totalPages : internalTotalPages) > 1 && (
//             <div className="mt-4 flex justify-end">
//               <Pagination
//                 currentPage={
//                   hasPropData
//                     ? currentPage
//                     : internalCurrentPage
//                 }
//                 totalPages={
//                   hasPropData
//                     ? totalPages
//                     : internalTotalPages
//                 }
//                 onPageChange={
//                   hasPropData
//                     ? setCurrentPage
//                     : setInternalCurrentPage
//                 }
//               />
//             </div>
//           )}
//         </>
//       )}
//     </SectionCard>
//   );
// }